/**
 * Magento Integration Factory
 * Creates and configures Magento integration instances
 */

import { MagentoIntegration } from './MagentoIntegration';
import { IntegrationConfig, ValidationResult } from '../../../types/integration';
import { IntegrationFactory } from '../registry/IntegrationRegistry';
import { BaseIntegration } from '../base/BaseIntegration';

export interface MagentoConnectionParams {
  baseUrl: string;
  adminToken: string;
  storeCode?: string;
  websiteId?: number;
  storeId?: number;
  version?: '2.3' | '2.4';
  enableGraphQL?: boolean;
  webhookSecret?: string;
}

export class MagentoIntegrationFactory implements IntegrationFactory {
  /**
   * Create a new Magento integration instance
   */
  static create(params: MagentoConnectionParams): MagentoIntegration {
    const config: IntegrationConfig = {
      id: `magento_${Date.now()}`,
      name: 'Magento Integration',
      type: 'ecommerce',
      platform: 'magento',
      status: 'inactive',
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 100,
        rateLimitPerMinute: 60,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {
          baseUrl: params.baseUrl.replace(/\/$/, ''), // Remove trailing slash
          adminToken: params.adminToken,
          storeCode: params.storeCode || 'default',
          websiteId: params.websiteId || 1,
          storeId: params.storeId || 1,
          version: params.version || '2.4',
          enableGraphQL: params.enableGraphQL || false,
          webhookSecret: params.webhookSecret
        }
      },
      credentials: {
        encrypted: JSON.stringify({ adminToken: params.adminToken }),
        keyId: 'default',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      },
      fieldMappings: [
        {
          id: 'product_id_mapping',
          sourceField: 'id',
          targetField: 'product_id',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        },
        {
          id: 'product_name_mapping',
          sourceField: 'name',
          targetField: 'title',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        },
        {
          id: 'customer_email_mapping',
          sourceField: 'email',
          targetField: 'customer_email',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        },
        {
          id: 'product_sku_mapping',
          sourceField: 'sku',
          targetField: 'product_sku',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        },
        {
          id: 'product_price_mapping',
          sourceField: 'price',
          targetField: 'product_price',
          direction: 'bidirectional',
          required: true,
          dataType: 'number'
        }
      ],
      webhookConfig: params.webhookSecret ? {
        url: '', // Will be set when registering webhooks
        events: [
          { type: 'product.created', description: 'Triggered when a new product is created', payload: {} },
          { type: 'product.updated', description: 'Triggered when a product is updated', payload: {} },
          { type: 'product.deleted', description: 'Triggered when a product is deleted', payload: {} },
          { type: 'customer.created', description: 'Triggered when a new customer is created', payload: {} },
          { type: 'customer.updated', description: 'Triggered when a customer is updated', payload: {} },
          { type: 'order.created', description: 'Triggered when a new order is placed', payload: {} },
          { type: 'order.updated', description: 'Triggered when an order status changes', payload: {} },
          { type: 'inventory.updated', description: 'Triggered when product inventory changes', payload: {} }
        ],
        secret: params.webhookSecret,
        retryPolicy: {
          maxAttempts: 3,
          baseDelay: 1000,
          maxDelay: 30000,
          backoffMultiplier: 2,
          retryableErrors: ['NETWORK_ERROR', 'TIMEOUT', 'RATE_LIMIT']
        },
        enabled: true
      } : undefined,
      syncSchedule: {
        enabled: true,
        frequency: 'hourly',
        timezone: 'UTC',
        batchSize: 100,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        issues: []
      },
      metadata: {
        description: 'Magento 2 e-commerce integration',
        tags: ['ecommerce', 'magento', 'sync'],
        category: 'E-commerce',
        version: '1.0.0',
        supportedFeatures: ['product_sync', 'customer_sync', 'order_sync', 'inventory_management'],
        limitations: ['Requires admin token', 'Rate limited by Magento API'],
        documentation: '/docs/integrations/magento',
        customData: {}
      }
    };

    return new MagentoIntegration(config);
  }

  /**
   * Create integration from existing configuration
   */
  static fromConfig(config: IntegrationConfig): MagentoIntegration {
    return new MagentoIntegration(config);
  }

  /**
   * Validate Magento connection parameters
   */
  static validateParams(params: MagentoConnectionParams): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate base URL
    if (!params.baseUrl) {
      errors.push('Base URL is required');
    } else {
      try {
        new URL(params.baseUrl);
      } catch {
        errors.push('Base URL must be a valid URL');
      }
    }

    // Validate admin token
    if (!params.adminToken) {
      errors.push('Admin token is required');
    } else if (params.adminToken.length < 10) {
      errors.push('Admin token appears to be invalid (too short)');
    }

    // Validate store code format
    if (params.storeCode && !/^[a-z0-9_]+$/.test(params.storeCode)) {
      errors.push('Store code must contain only lowercase letters, numbers, and underscores');
    }

    // Validate website and store IDs
    if (params.websiteId !== undefined && (params.websiteId < 0 || !Number.isInteger(params.websiteId))) {
      errors.push('Website ID must be a non-negative integer');
    }

    if (params.storeId !== undefined && (params.storeId < 0 || !Number.isInteger(params.storeId))) {
      errors.push('Store ID must be a non-negative integer');
    }

    // Validate version
    if (params.version && !['2.3', '2.4'].includes(params.version)) {
      errors.push('Version must be either 2.3 or 2.4');
    }

    // Validate webhook secret if provided
    if (params.webhookSecret && params.webhookSecret.length < 16) {
      errors.push('Webhook secret must be at least 16 characters long');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Test connection with the provided parameters
   */
  static async testConnection(params: MagentoConnectionParams): Promise<{ success: boolean; error?: string; storeInfo?: any }> {
    const validation = this.validateParams(params);
    if (!validation.isValid) {
      return {
        success: false,
        error: `Invalid parameters: ${validation.errors.join(', ')}`
      };
    }

    try {
      // Get store information to verify connection
      const baseUrl = `${params.baseUrl}/rest/${params.storeCode || 'default'}/V1`;
      const response = await fetch(`${baseUrl}/store/storeConfigs`, {
        headers: {
          'Authorization': `Bearer ${params.adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const storeInfo = await response.json();
      
      return {
        success: true,
        storeInfo: Array.isArray(storeInfo) ? storeInfo[0] : storeInfo
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get default field mappings for Magento
   */
  static getDefaultFieldMappings() {
    return [
      // Product mappings
      {
        sourceField: 'id',
        targetField: 'product_id',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Product ID mapping'
      },
      {
        sourceField: 'sku',
        targetField: 'product_sku',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Product SKU mapping'
      },
      {
        sourceField: 'name',
        targetField: 'title',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Product name/title mapping'
      },
      {
        sourceField: 'price',
        targetField: 'product_price',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'number' as const,
        description: 'Product price mapping'
      },
      {
        sourceField: 'status',
        targetField: 'product_status',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Product status mapping'
      },
      
      // Customer mappings
      {
        sourceField: 'id',
        targetField: 'customer_id',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Customer ID mapping'
      },
      {
        sourceField: 'email',
        targetField: 'customer_email',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Customer email mapping'
      },
      {
        sourceField: 'firstname',
        targetField: 'first_name',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Customer first name mapping'
      },
      {
        sourceField: 'lastname',
        targetField: 'last_name',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Customer last name mapping'
      },
      
      // Order mappings
      {
        sourceField: 'entity_id',
        targetField: 'order_id',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Order ID mapping'
      },
      {
        sourceField: 'increment_id',
        targetField: 'order_number',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Order number mapping'
      },
      {
        sourceField: 'grand_total',
        targetField: 'total_price',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'number' as const,
        description: 'Order total mapping'
      },
      {
        sourceField: 'status',
        targetField: 'order_status',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Order status mapping'
      }
    ];
  }

  /**
   * Get supported webhook events for Magento
   */
  static getSupportedWebhookEvents() {
    return [
      {
        event: 'product.created',
        description: 'Triggered when a new product is created'
      },
      {
        event: 'product.updated',
        description: 'Triggered when a product is updated'
      },
      {
        event: 'product.deleted',
        description: 'Triggered when a product is deleted'
      },
      {
        event: 'customer.created',
        description: 'Triggered when a new customer is created'
      },
      {
        event: 'customer.updated',
        description: 'Triggered when a customer is updated'
      },
      {
        event: 'order.created',
        description: 'Triggered when a new order is placed'
      },
      {
        event: 'order.updated',
        description: 'Triggered when an order status changes'
      },
      {
        event: 'inventory.updated',
        description: 'Triggered when product inventory changes'
      }
    ];
  }

  /**
   * Generate webhook configuration for Magento
   */
  static generateWebhookConfig(baseWebhookUrl: string, secret: string) {
    const events = this.getSupportedWebhookEvents();
    
    return events.map(event => ({
      event: event.event,
      url: `${baseWebhookUrl}/magento/${event.event}`,
      secret,
      active: true,
      description: event.description
    }));
  }

  // Instance methods required by IntegrationFactory interface
  
  async create(config: IntegrationConfig): Promise<BaseIntegration> {
    return MagentoIntegrationFactory.fromConfig(config);
  }

  async validate(config: Partial<IntegrationConfig>): Promise<ValidationResult> {
    const customSettings = config.settings?.customSettings as MagentoConnectionParams;
    
    if (!customSettings) {
      return {
        valid: false,
        errors: [{ field: 'settings', message: 'Magento configuration is required', code: 'REQUIRED' }],
        warnings: []
      };
    }

    const validation = MagentoIntegrationFactory.validateParams(customSettings);
    
    return {
      valid: validation.isValid,
      errors: validation.errors.map(error => ({ field: 'config', message: error, code: 'VALIDATION_ERROR' })),
      warnings: []
    };
  }

  getConfigSchema(): Record<string, any> {
    return {
      type: 'object',
      properties: {
        baseUrl: {
          type: 'string',
          format: 'uri',
          title: 'Magento Base URL',
          description: 'The base URL of your Magento store'
        },
        adminToken: {
          type: 'string',
          title: 'Admin Token',
          description: 'Admin access token for API authentication',
          minLength: 10
        },
        storeCode: {
          type: 'string',
          title: 'Store Code',
          description: 'Store code for multi-store setups',
          default: 'default',
          pattern: '^[a-z0-9_]*$'
        },
        websiteId: {
          type: 'number',
          title: 'Website ID',
          description: 'Website ID for multi-website setups',
          default: 1,
          minimum: 0
        },
        storeId: {
          type: 'number',
          title: 'Store ID',
          description: 'Store ID for multi-store setups',
          default: 1,
          minimum: 0
        },
        version: {
          type: 'string',
          title: 'Magento Version',
          description: 'Your Magento version',
          enum: ['2.3', '2.4'],
          default: '2.4'
        },
        enableGraphQL: {
          type: 'boolean',
          title: 'Enable GraphQL',
          description: 'Use GraphQL API for enhanced performance',
          default: false
        },
        webhookSecret: {
          type: 'string',
          title: 'Webhook Secret',
          description: 'Secret key for webhook signature verification',
          minLength: 16
        }
      },
      required: ['baseUrl', 'adminToken']
    };
  }

  getSupportedFeatures(): string[] {
    return [
      'product_sync',
      'customer_sync',
      'order_sync',
      'inventory_management',
      'webhook_support',
      'real_time_sync',
      'batch_operations',
      'field_mapping',
      'conflict_resolution',
      'graphql_support'
    ];
  }
}